<script setup>
import { ref, nextTick } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import MediaflowService from '@/services/MediaflowService'
import AddPlayoutErrorAlert from '@/components/TVPlayout/AddPlayoutErrorAlert.vue'
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import ModalBox from './ModalBox.vue'

const store = useStore()
const { notify } = useNotification()

const isVisible = ref(false)
const error = ref(null)
const processing = ref(false)
const nameInput = ref(null)

const mediaflow = ref({
	name: '',
})
const selectedStream = ref(null)

const emit = defineEmits(['mediaflow-added'])

const onReset = () => {
	mediaflow.value = { name: '' }
	error.value = null
	selectedStream.value = null
	processing.value = false
}

// Kept for API compatibility with the dashboard (`showMediaflowModal.toggleSidebar()`).
const toggleSidebar = async () => {
	isVisible.value = !isVisible.value
	if (isVisible.value) {
		onReset()
		await nextTick()
		nameInput.value?.focus?.()
	}
}

const createMediaflow = async () => {
	if (!mediaflow.value.name.trim() || processing.value) return
	processing.value = true
	try {
		store.dispatch('Ui/setLoadingStatus', true)
		const newPayout = await MediaflowService.createMediaflow(
			mediaflow.value.name,
			selectedStream.value?._id ?? null
		)
		emit('mediaflow-added', newPayout)
		notify({ group: 'success', text: 'Cloud production created.' })
		isVisible.value = false
		store.dispatch('Ui/setLoadingStatus', false)
	} catch (err) {
		console.log(err)
		error.value = err.message
		store.dispatch('Ui/setLoadingStatus', false)
	}
	processing.value = false
}

defineExpose({ toggleSidebar })
</script>

<template>
	<ModalBox v-model="isVisible" title="New Cloud Production" size="sm" placement="center" mobile-placement="bottom" header-class="!px-5 !pt-5 !pb-3" @close="onReset">
		<form class="flex flex-col gap-3.5 px-5" @submit.prevent="createMediaflow">
			<p class="m-0 text-sm text-surface-8">One output, several scenes to switch between. It runs on Castr's servers once live.</p>
			<AddPlayoutErrorAlert v-if="error" :error="error" />
			<FormInput
				id="mediaflow-name"
				ref="nameInput"
				v-model="mediaflow.name"
				label="Name"
				placeholder="e.g. Sunday service, Weekly show"
				:maxlength="120"
				@keydown.enter.prevent="createMediaflow"
			/>
		</form>
		<template #footer>
			<div class="flex flex-col-reverse gap-2 px-5 pb-5 pt-4 sm:flex-row sm:justify-end">
				<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="processing" @click="isVisible = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!mediaflow.name.trim()" :is-loading="processing" data-testid="create-production" @click="createMediaflow">Create</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
